import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { menuHighlights, menuLookupKeys, popularMenuIds } from "../data/mock";
import { Plus, Flame, Search, Sparkles, Leaf, Baby, Mic, Loader2 } from "lucide-react";
import { useVoiceInput } from "../hooks/useVoiceInput";
import { useCart } from "../context/CartContext";
import BlurImage from "./BlurImage";
import DishDetailModal from "./DishDetailModal";
import DishRatingPill, { RatingsProvider } from "./DishRatings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_ORDER = ["Signature", "Liberian", "West African", "Grill", "Wraps", "Comfort", "Sides", "Kids"];

function priceToNumber(p) {
  return parseFloat((p || "0").replace(/[^\d.]/g, "")) || 0;
}
function fmtPrice(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "$0";
  return `$${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)}`;
}

// ---- Dietary badge inference ---------------------------------------------
// Restaurant sites like Din Tai Fung / Nusr-Et use icon rows to communicate
// at-a-glance dietary info. We derive tags from item description + tag so
// the operator doesn't have to fill anything new — the strings mama already
// writes drive the badges.
function inferDietary(item) {
  const text = `${item.name || ""} ${item.description || ""}`.toLowerCase();
  const badges = [];
  const spicy =
    /\bhot\b|\bspicy\b|\bpepper\b|\bchili|\bchilli|habanero|jalape|scotch bonnet/.test(text);
  if (spicy) badges.push({ key: "spicy", label: "Spicy", icon: Flame });
  const kidsTag = (item.tag || "").toLowerCase() === "kids";
  if (kidsTag) badges.push({ key: "kids", label: "Kids friendly", icon: Baby });
  return badges;
}

export default function MenuHighlights() {
  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");
  const [overrides, setOverrides] = useState({});
  const [extraItems, setExtraItems] = useState([]);
  const [catOrder, setCatOrder] = useState(DEFAULT_ORDER);
  const [openItem, setOpenItem] = useState(null);
  const { add } = useCart();
  const voice = useVoiceInput({ onText: (t) => setQuery(t) });

  useEffect(() => {
    axios.get(`${API}/menu`).then(({ data }) => {
      const map = {};
      const staticIds = new Set(menuHighlights.map((m) => m.id));
      const extras = [];
      (data.items || []).forEach((o) => {
        map[o.id] = o;
        if (!staticIds.has(o.id) && o.name) {
          extras.push({
            id: o.id,
            name: o.name,
            description: o.description || "",
            price: o.price != null ? fmtPrice(o.price) : "$0",
            tag: o.tag || "Signature",
            image: o.image || null,
            lookup_key: o.lookup_key,
            popular: !!o.popular,
            sold_out: !!o.sold_out,
          });
        }
      });
      setOverrides(map);
      setExtraItems(extras);
    }).catch(() => { setOverrides({}); setExtraItems([]); });

    axios.get(`${API}/menu/categories`).then(({ data }) => {
      if (Array.isArray(data.order) && data.order.length) setCatOrder(data.order);
    }).catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...catOrder], [catOrder]);

  const merged = useMemo(() => {
    const mocks = menuHighlights.map((item) => {
      const o = overrides[item.id] || {};
      return {
        ...item,
        name: o.name || item.name,
        description: o.description || item.description,
        image: o.image != null ? o.image : item.image,
        tag: o.tag || item.tag,
        price: o.price != null ? fmtPrice(o.price) : item.price,
        sold_out: !!o.sold_out,
        popular: o.popular != null ? !!o.popular : popularMenuIds.includes(item.id),
      };
    });
    const rank = new Map(catOrder.map((c, i) => [c, i]));
    const all = [...mocks, ...extraItems];
    all.sort((a, b) => {
      const ra = rank.has(a.tag) ? rank.get(a.tag) : 999;
      const rb = rank.has(b.tag) ? rank.get(b.tag) : 999;
      if (ra !== rb) return ra - rb;
      return (a.id || 0) - (b.id || 0);
    });
    return all;
  }, [overrides, extraItems, catOrder]);

  // Chef's picks — top 3 popular, in-stock dishes. Shown as a distinct row
  // when the customer is on "All" with no active search (i.e. their first
  // moment on the menu, when decision paralysis is highest).
  const chefsPicks = useMemo(
    () => merged.filter((m) => m.popular && !m.sold_out && m.image).slice(0, 3),
    [merged]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return merged.filter((m) => {
      if (active !== "All" && m.tag !== active) return false;
      if (!q) return true;
      return (
        (m.name || "").toLowerCase().includes(q) ||
        (m.description || "").toLowerCase().includes(q) ||
        (m.tag || "").toLowerCase().includes(q)
      );
    });
  }, [merged, active, query]);

  const showChefsPicks = active === "All" && !query.trim() && chefsPicks.length >= 3;

  const allLookupKeys = useMemo(
    () => merged.map((m) => m.lookup_key || menuLookupKeys[m.id]).filter(Boolean),
    [merged]
  );

  return (
    <RatingsProvider lookupKeys={allLookupKeys}>
    <section id="menu" className="py-24 md:py-36 bg-[var(--mm-bg-alt)] border-t border-b border-[var(--mm-line)]">
      <div className="max-w-[1300px] mx-auto px-6 md:px-12">
        <div className="text-center mb-14 fade-in">
          <div className="section-label mb-4">The Kitchen</div>
          <h2 className="font-display text-5xl md:text-7xl italic font-light text-[var(--mm-cream)] leading-[0.95]">
            Menu <span className="text-[var(--mm-accent)]">Highlights</span>
          </h2>
          <p className="text-[var(--mm-cream-muted)] mt-6 max-w-xl mx-auto font-light">
            A taste of what we serve. The full menu is presented at the table — seasonal, ever-changing, always generous.
          </p>
        </div>

        {/* Chef's picks — high-impact first row that reduces decision fatigue.
            Hidden when the customer starts filtering because it becomes noise. */}
        {showChefsPicks && (
          <ChefsPicksRow picks={chefsPicks} onAdd={add} />
        )}

        {/* Search + category filters row */}
        <div className="mb-10 fade-in flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="relative flex-1 max-w-md" data-testid="menu-search-wrap">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mm-cream)]/45"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={voice.recording ? "Listening…" : "Search dishes, ingredients…"}
              data-testid="menu-search-input"
              className={`w-full bg-[var(--mm-bg)]/60 border border-[var(--mm-line)] focus:border-[var(--mm-accent)] outline-none pl-9 py-2.5 text-sm text-[var(--mm-cream)] placeholder:text-[var(--mm-cream)]/40 tracking-wide ${voice.supported ? "pr-11" : "pr-3"}`}
            />
            {voice.supported && (
              <button
                type="button"
                onClick={voice.toggle}
                data-testid="menu-voice-btn"
                aria-label={voice.recording ? "Stop voice search" : "Search by voice"}
                title="Search by voice"
                className={`absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center transition-colors ${
                  voice.recording
                    ? "text-red-500 animate-pulse"
                    : "text-[var(--mm-cream)]/45 hover:text-[var(--mm-accent)]"
                }`}
              >
                {voice.busy ? <Loader2 size={15} className="animate-spin text-[var(--mm-accent)]" /> : <Mic size={15} />}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`px-4 py-2 text-[0.72rem] tracking-[0.22em] uppercase border transition-all duration-300 ${
                  active === c
                    ? "bg-[var(--mm-accent)] text-[var(--mm-bg)] border-[var(--mm-accent)]"
                    : "border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)]"
                }`}
                data-testid={`menu-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="text-center text-[var(--mm-cream-muted)] py-16 fade-in visible" data-testid="menu-empty">
            No dishes match &ldquo;{query}&rdquo; in <em>{active}</em>. Try another word or reset the category.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((item) => (
              <DishCard
                key={item.id}
                item={item}
                onAdd={add}
                onOpen={() => setOpenItem({ ...item, lookup_key: item.lookup_key || menuLookupKeys[item.id] })}
              />
            ))}
          </div>
        )}

        <div className="mt-16 text-center fade-in flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a href="#reservation" className="btn-outline">Book a Table to Taste</a>
          <a href="#order" className="btn-primary">Order Online</a>
        </div>
      </div>

      <DishDetailModal
        item={openItem}
        open={!!openItem}
        onClose={() => setOpenItem(null)}
      />
    </section>
    </RatingsProvider>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function ChefsPicksRow({ picks, onAdd }) {
  return (
    <div className="mb-16 fade-in visible" data-testid="chefs-picks">
      <div className="flex items-center gap-3 mb-6">
        <Sparkles size={16} className="text-[var(--mm-accent)]" />
        <p className="text-[0.66rem] tracking-[0.3em] uppercase text-[var(--mm-accent)]">
          Chef&apos;s picks · start here
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {picks.map((it, idx) => {
          const lookup_key = it.lookup_key || menuLookupKeys[it.id];
          return (
            <button
              key={it.id}
              type="button"
              onClick={() =>
                lookup_key &&
                onAdd({
                  lookup_key,
                  name: it.name,
                  price: priceToNumber(it.price),
                  image: it.image,
                })
              }
              className="group relative flex items-center gap-4 bg-[var(--mm-bg)]/50 border border-[var(--mm-line)] hover:border-[var(--mm-accent)] transition-all duration-300 p-3 text-left"
              data-testid={`chefs-pick-${idx + 1}`}
            >
              <BlurImage
                src={it.image}
                alt={it.name}
                className="w-20 h-20 object-cover shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[0.58rem] tracking-[0.3em] uppercase text-[var(--mm-accent)] mb-1">
                  #{idx + 1} <span className="text-[var(--mm-cream)]/40">·</span> {it.tag}
                </div>
                <p className="font-display italic text-lg text-[var(--mm-cream)] leading-tight truncate">
                  {it.name}
                </p>
                <p className="text-xs text-[var(--mm-cream-muted)] mt-1">
                  {it.price} · <span className="text-[var(--mm-accent)] group-hover:underline">Add</span>
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DishCard({ item, onAdd, onOpen }) {
  const lookup_key = item.lookup_key || menuLookupKeys[item.id];
  const numericPrice = priceToNumber(item.price);
  const dietary = inferDietary(item);
  const isPopular = !!item.popular;

  const openIfNotSoldOut = () => { if (!item.sold_out) onOpen?.(); };

  return (
    <article
      className="group fade-in relative cursor-pointer"
      data-testid={`dish-${item.id}`}
      onClick={openIfNotSoldOut}
    >
      {item.sold_out && (
        <div className="absolute inset-0 z-20 bg-black/60 backdrop-blur-[2px] flex items-center justify-center">
          <div className="border-2 border-[var(--mm-accent)] px-6 py-3 -rotate-6 bg-[var(--mm-bg)]/80">
            <div className="font-display italic text-3xl text-[var(--mm-accent)]">Sold Out</div>
            <div className="text-[0.62rem] tracking-[0.3em] uppercase text-[var(--mm-cream)] text-center mt-1">Back soon</div>
          </div>
        </div>
      )}
      {isPopular && !item.sold_out && (
        <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-3 py-1 bg-[var(--mm-accent)] text-[var(--mm-bg)] text-[0.62rem] tracking-[0.24em] uppercase font-semibold">
          <Flame size={11} className="fill-current" /> Popular
        </div>
      )}
      {item.image ? (
        <div className="aspect-[4/5] overflow-hidden mb-5">
          <BlurImage
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      ) : (
        <div className="aspect-[4/5] overflow-hidden mb-5 bg-[var(--mm-bg-alt)] border border-[var(--mm-line)] flex items-center justify-center p-8">
          <div className="text-center">
            <div className="font-display italic text-4xl md:text-5xl text-[var(--mm-accent)] mb-3 leading-tight">
              {item.name.split(" & ")[0].split(" ")[0]}
            </div>
            <div className="text-[0.68rem] tracking-[0.3em] uppercase text-[var(--mm-cream-muted)]">
              Photo coming soon
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between items-start gap-4">
        <div>
          <div className="text-[0.66rem] tracking-[0.3em] uppercase text-[var(--mm-accent)] mb-2">{item.tag}</div>
          <h3 className="font-display text-2xl text-[var(--mm-cream)] italic font-light leading-tight">{item.name}</h3>
          {lookup_key && (
            <div className="mt-1.5">
              <DishRatingPill lookup_key={lookup_key} />
            </div>
          )}
        </div>
        <div className="font-display text-xl text-[var(--mm-accent)] shrink-0">{item.price}</div>
      </div>
      <p className="text-sm text-[var(--mm-cream-muted)] mt-3 leading-relaxed font-light">{item.description}</p>
      {dietary.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid={`dish-${item.id}-badges`}>
          {dietary.map(({ key, label, icon: Icon }) => (
            <span
              key={key}
              className="inline-flex items-center gap-1 text-[0.58rem] tracking-[0.24em] uppercase text-[var(--mm-cream)]/70 border border-[var(--mm-line)] px-2 py-1"
            >
              <Icon size={9} strokeWidth={1.8} /> {label}
            </span>
          ))}
        </div>
      )}
      {lookup_key && !item.sold_out && (
        <button
          onClick={() =>
            onAdd({
              lookup_key,
              name: item.name,
              price: numericPrice,
              image: item.image,
            })
          }
          className="mt-5 inline-flex items-center gap-2 text-[0.72rem] tracking-[0.24em] uppercase text-[var(--mm-cream)] border border-[var(--mm-line)] px-4 py-2 hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] hover:border-[var(--mm-accent)] transition-all duration-300"
          data-testid={`add-${item.id}`}
        >
          <Plus size={14} /> Add to Order
        </button>
      )}
    </article>
  );
}
